import { TextSubheader } from "../../components/text/text-subheader";
import ApplicationCooLayout from "../../layout/application-coo-layout";
import { useAppSelector } from "../../redux/hook";
import { P2Button, P2Alert } from "react-bootstrap-common-script";
import { useNavigate } from "react-router-dom";
import { useState, useCallback, useEffect, useMemo } from "react";
import { useGetStagesApplicationQuery } from "../../redux/services/getStagesApplicationStatus";
import { Helpers } from "../../utils/helper";
import GatherConsentTable from "../../components/table/gather-consent-table";
import { useInitialWindowScrollTo } from "../../hooks/use-initial-window-scroll-to";
import { Separator } from "../../components/separator";
import { PolymerAxios } from "../../utils/axios";
import moment from "moment";
import { API_ApplicationDetail, API_CompleteConsent, API_OverrideEligibilityCheck } from "../../utils/api";
import { stageBTrackingId } from "../../utils/wogaa-ids";
import { customWindow } from "../../models/interfaces/wogaa.interface";
import { EligibilityCheck, EligibilityOverride } from "../../models/classes/EligibilityOverride.class";
import { useGetApplicationConsentByUuidQuery } from "../../redux/services/getApplicationConsent";
import { PolymerCrypto } from "../../utils/encryptor";

interface ConsentPageProps {
  stage: string;
}

const ConsentPage = ({ ...props }: ConsentPageProps) => {
  const navigate = useNavigate();
  const queryParameters = new URLSearchParams(window.location.search);
  const application_id = queryParameters.get("application_id");
  const loginUser = useAppSelector(
    (state) => state.auth.AuthenticationSlice.loginUser
  );
  const startApplicationFormData = useAppSelector(
    (state) => state.app.ApplicationStartSlice.formData
  );
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);

  const { data } = useGetStagesApplicationQuery({ application_id: application_id ?? "0", uinfin: loginUser?.uinfin ?? "", singpass_token: singpassToken ?? "" }, { skip: application_id === "0" || application_id === null });
  const { data: consentApplication, isLoading } = useGetApplicationConsentByUuidQuery({applicationUuid: application_id ?? "0", uinfin: loginUser?.uinfin ?? "", singpass_token: singpassToken ?? ""}, { skip: application_id === "0" || application_id === null });
  const [submittedDate, setSubmittedDate] = useState<string>("");
  const applicationId = useAppSelector(
    (state) => state.app.ApplicationIdSlice.applicationId
  );
  const [consentComplete, setConsentComplete] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    PolymerAxios(loginUser?.uinfin ?? "", singpassToken).get(`${API_ApplicationDetail}?applicationUuid=${application_id}`).then((res) => {
      setSubmittedDate(res.data.last_updated_at);
    }).catch((error) => {
      navigate("/dashboard");
    })
  }, [])

  useEffect(() => {
    if(!application_id || application_id == "undefined" || application_id !== applicationId){
      window.location.href = "/transfer/dashboard";
    }
  }, [application_id])

  const { date, time } = useMemo(() => {
    if(submittedDate.length > 0){
      return {date: moment(submittedDate).format('DD MMMM yyyy'), time: moment(submittedDate).format('hh:mma')}
    }else{
      return {date: moment().format('DD MMMM yyyy'), time: moment().format('hh:mma')}
    }
    
  }, [submittedDate])

  useInitialWindowScrollTo();

  const completeConsent = useCallback(async () => {
    await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).patch(`${API_CompleteConsent}?applicationUuid=${application_id}`);

    const overrideEligibility = new EligibilityOverride({ application_uuid: application_id, individuals: [] });

    consentApplication?.owners_consent_status.forEach(owner => {
      // Skip for exempted owners
      if (owner.consent_check === EligibilityCheck.EXEMPT) return;
      
      // Mark owners that have not given consent as bypass
      if (!owner.consent_given) {
        overrideEligibility.individuals?.push({nric: PolymerCrypto.decryption(owner.uin_fin), consent_check: EligibilityCheck.BYPASS})
      }
    })
    await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_OverrideEligibilityCheck, overrideEligibility);
    window.location.href = `/transfer/application-coo?application_id=${applicationId}`
  }, [applicationId, navigate]);

  const handleNavigation = useCallback(() => {
    window.location.href = `/transfer/application-coo?application_id=${applicationId}`
  },[applicationId, navigate])

  useEffect(() => {
    if (props.stage === 'B' && customWindow.wogaaCustom) {
      customWindow.wogaaCustom.completeTransactionalService(stageBTrackingId);
    }
  }, []);

  return (
    <ApplicationCooLayout>
      <div style={{ padding: "62px 42px 120px" }}>
        <TextSubheader
          subheaderMain={`Stage ${props.stage}: Gather Consent`}
          subheaderSub={startApplicationFormData.selected_hdb_property?.address}
        />
        <div className="polymer-gather-consent-main-column">
         {consentComplete && data?.application_status && data.application_status === "SUBMIT_COMPLETE" ? (
            <>
              {" "}
              <div style={{ marginTop: "2rem", width: "100%" }}>
                <P2Alert
                  style={{ boxShadow: "0px 2px 8px 0px #00000026" }}
                  variant={"success"}
                  text={
                    "All current and proposed owners have electronically endorsed the application. You may proceed to make payment to submit the application to HDB."
                  }
                ></P2Alert>
              </div>
              <div className="polymer-button-wrapper">
                <P2Button
                  variant="primary-1"
                  disabled={!consentComplete}
                  onClick={completeConsent}
                >
                  Continue
                </P2Button>
              </div>
              <Separator style={{ width: "100%" }} />
            </>
          ) : <div style={{marginBottom: "16px"}}></div>}

          {consentComplete === false && <div style={{ marginTop: "2rem", width: "100%" }}>
            <P2Alert
              style={{ boxShadow: "0px 2px 8px 0px #00000026" }}
              variant={"warning"}
              text={"Please inform the other owners to give their consent to the application."}
            ></P2Alert>
          </div>}
          
          {!isLoading ? (
            <GatherConsentTable
              applicationSubmitted={`Application created on ${date}, at ${time}`}
              timeRemaining={`${Helpers.formatGetDayLeft(data?.stage_b.deadline.toString() ?? "")} days left for everyone to give their consent.`}
              setConsentComplete={setConsentComplete}
              consentApplication={consentApplication}
            />
          ) : null}
          
          {!consentComplete && (
            <div className="polymer-button-wrapper">
              <P2Button
                variant="primary-1"
                onClick={handleNavigation}
              >
                Back
              </P2Button>
            </div>
          )}
        </div>
      </div>
    </ApplicationCooLayout>
  );
};

export default ConsentPage;
